// Copyright 2022 Sorama B.V.

import { autoinject } from "aurelia-dependency-injection";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { CamIv64Device } from "./CamIV64Helpers";
import { CamIV64SelectionManager } from "./CamIV64SelectionManager";

/**
 * Responsible for finding camIV64s (this exists just to be able to use the HardwareService Interface)
 * Currently only ever returns a single cam
 */
@autoinject
export class CamIV64Service implements IHardwareService<CamIv64Device> {
    public hardware: CamIv64Device[] = [];
    protected hardwarePromise: Promise<void>;
    public isRefreshing: boolean;

    public api: ICamApi;
    private apiUri: () => Promise<string>;

    constructor(
        api: ICamApi,
        private readonly webRequestHandler: IWebRequestHandler,
        baseUri: () => Promise<string>
    ) {
        this.api = api;
        this.apiUri = baseUri;
        this.refresh();
    }

    // Code copied from other hardwareService
    public getHardwareById(id: string): Promise<CamIv64Device> {
        const foundCam = this.hardware.find((cam) => cam.id === id);
        if (foundCam) return Promise.resolve(foundCam);
        return Promise.reject(`Could not find CAM with id: ${id}`);
    }

    public async getHardwareByIdOrRefresh(id: string): Promise<CamIv64Device> {
        try {
            return this.getHardwareById(id);
        } catch (e) {
            await this.refresh();
            return this.getHardwareById(id);
        }
    }

    public get selectedDeviceId(): string {
        return CamIV64SelectionManager.selectedDeviceId;
    }

    public set selectedDeviceId(deviceId: string) {
        CamIV64SelectionManager.selectedDeviceId = deviceId;
    }

    public async refresh(): Promise<void> {
        this.isRefreshing = true;
        // Make sure that we're piggybacking already pending refresh calls.
        if (this.hardwarePromise) {
            return this.hardwarePromise;
        }

        return this.apiUri()
            .then((uri) => {
                return (this.hardwarePromise = this.webRequestHandler
                    .createGetRequest<string[]>(uri + "/IV64")
                    .send()
                    .then((res) => {
                        this.hardware = res.content.map((x) => {
                            return new CamIv64Device(x);
                        });
                        const hardwareIds = this.hardware.map((x) => x.id);

                        // Remove current selection if cam is unavailable and set to preferred device
                        if (!hardwareIds.includes(this.selectedDeviceId)) {
                            CamIV64SelectionManager.selectedDeviceId = "";
                            if (hardwareIds.includes(CamIV64SelectionManager.preferredDevice)) {
                                CamIV64SelectionManager.selectedDeviceId = CamIV64SelectionManager.preferredDevice;
                            }
                        }

                        // Just one CAM, select it for that sweet sweet UX
                        if (this.hardware.length == 1) {
                            CamIV64SelectionManager.selectedDeviceId = this.hardware[0].id;
                        }
                    })
                    .catch(() => {
                        this.hardware = [];
                    })
                    .finally(() => {
                        this.isRefreshing = false;
                        this.hardwarePromise = null;
                    }));
            })
            .catch(() => {
                this.isRefreshing = false;
                this.hardwarePromise = null;
                this.hardware = [];
            });
    }
}
